/**
 * WIP and recovery — the managers' Time & Billing reports (T&B-1 PR 4; spec,
 * "Reports").
 *
 *   WIP                    unbilled approved work, aged Current/30/60/90/120+
 *   Recovery & write-off   per fixed-price period: price against time value
 *   Fixed price vs time    the same periods: price against time at cost
 *   Productivity           chargeable hours against available hours
 *
 * Every figure is the server's, recomputed from approved entries and the
 * values stamped on them at approval; this page only lays them out, groups
 * them and exports what it shows (CSV, print) -- the same rule as the due
 * list. Managers only (decision 5).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AGE_BANDS,
  api,
  type AuthState,
  type FixedPeriodRow,
  type ObligationTypeInUse,
  type ProductivityReport,
  type ProductivitySpan,
  type RecoveryReport,
  type WipLine,
  type WipReport,
} from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { clientLabel, inputCls, labelCls, quietBtn, rand, todaySast } from '../components/TimeCapture'
import { downloadCsv } from '../csv'

type Tab = 'wip' | 'recovery' | 'margin' | 'productivity'
const TABS: { id: Tab; label: string }[] = [
  { id: 'wip', label: 'WIP' },
  { id: 'recovery', label: 'Recovery & write-off' },
  { id: 'margin', label: 'Fixed price vs time' },
  { id: 'productivity', label: 'Productivity' },
]

export const sectionCls = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'
export const thCls = 'px-2 py-1 text-left font-semibold'
export const tdCls = 'px-2 py-1.5 align-top'
const numCls = `${tdCls} text-right tabular-nums whitespace-nowrap`
const numTh = `${thCls} text-right`

/** Integer cents as plain decimals for a spreadsheet ("1250.00"). */
export const csvRand = (cents: number) => `${cents < 0 ? '-' : ''}${Math.floor(Math.abs(cents) / 100)}.${String(Math.abs(cents) % 100).padStart(2, '0')}`
const hoursOf = (hundredths: number) => (hundredths / 100).toFixed(2).replace(/\.?0+$/, '') || '0'
const pct = (bp: number | null) => (bp === null ? '—' : `${(bp / 100).toFixed(1)} %`)
const KIND_WORDS: Record<WipLine['kind'], string> = {
  fixed: 'Fixed price',
  hourly: 'Hourly',
  unpriced: 'No agreed price',
  disbursement: 'Disbursement',
}

export function Toolbar({ children, onCsv }: { children?: React.ReactNode; onCsv: () => void }) {
  return (
    <div className="mb-3 flex flex-wrap items-end gap-3 print:hidden">
      {children}
      <span className="ml-auto flex gap-2">
        <button type="button" className={quietBtn} onClick={onCsv}>
          Export CSV
        </button>
        <button type="button" className={quietBtn} onClick={() => window.print()}>
          Print
        </button>
      </span>
    </div>
  )
}

export function useReport<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setData(null)
    setError(null)
    load().then(
      (d) => live && setData(d),
      (e: unknown) => live && setError(e instanceof Error ? e.message : String(e))
    )
    return () => {
      live = false
    }
  }, [load])
  return { data, error }
}

export function ReportsPage({ obligationTypes, auth }: { obligationTypes: ObligationTypeInUse[]; auth: AuthState }) {
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'wip') as Tab
  const signedInManager = auth.mode === 'cognito' && auth.user?.role === 'manager'
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k)
      else next.set(k, v)
    }
    setParams(next, { replace: true })
  }

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time"
      title="WIP and recovery"
      subtitle="From approved time and the values stamped on it at approval. A rate changed later moves nothing here. Amounts exclude VAT."
    >
      {auth.mode === 'anonymous' ? (
        <ErrorState message="The reports show everyone's time, so they need sign-in. This server is running without Cognito (local development)." />
      ) : !signedInManager ? (
        <ErrorState message="Only a manager may see WIP and recovery." />
      ) : (
        <>
          <div className="mb-4 flex gap-1 border-b border-eq-border print:hidden" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => set({ tab: t.id === 'wip' ? null : t.id })}
                className={[
                  '-mb-px border-b-2 px-3 py-2 font-heading text-[13px] font-bold',
                  tab === t.id ? 'border-eq-navy text-eq-navy' : 'border-transparent text-eq-neutral hover:text-eq-navy',
                ].join(' ')}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'wip' && <WipTab params={params} set={set} />}
          {(tab === 'recovery' || tab === 'margin') && <RecoveryTab params={params} set={set} margin={tab === 'margin'} />}
          {tab === 'productivity' && <ProductivityTab params={params} set={set} />}
        </>
      )}
    </AppShell>
  )
}

type TabProps = { params: URLSearchParams; set: (patch: Record<string, string | null>) => void }

// ---------------------------------------------------------------------------
// WIP
// ---------------------------------------------------------------------------

type GroupBy = 'client' | 'job' | 'person'

function WipTab({ params, set }: TabProps) {
  const groupBy = (['client', 'job', 'person'].includes(params.get('by') ?? '') ? params.get('by') : 'client') as GroupBy
  const load = useCallback(() => api.wipReport(), [])
  const { data, error } = useReport<WipReport>(load)

  const groups = useMemo(() => {
    if (data === null) return []
    const map = new Map<string, { label: string; lines: WipLine[] }>()
    for (const l of data.lines) {
      const [key, label] =
        groupBy === 'client'
          ? [l.client.id, clientLabel(l.client)]
          : groupBy === 'job'
            ? [l.job?.id ?? 'disbursements', l.job?.name ?? 'Disbursements']
            : l.personId === null
              ? ['fixed', 'Fixed fees (per client job, not per person)']
              : [l.personId, l.people[0] ?? '—']
      const g = map.get(key) ?? { label, lines: [] }
      g.lines.push(l)
      map.set(key, g)
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [data, groupBy])

  const csv = () => {
    if (data === null) return
    downloadCsv(
      `wip-${data.asAt}.csv`,
      ['Client number', 'Client', 'Kind', 'Billing job', 'Period or date', 'People', 'Hours', 'Time value', 'WIP', 'Age band', 'Detail'],
      data.lines.map((l) => [
        String(l.client.clientNumber),
        l.client.shortName,
        KIND_WORDS[l.kind],
        l.job?.name ?? '',
        l.label,
        l.people.join('; '),
        l.hours ?? '',
        csvRand(l.valueCents),
        csvRand(l.wipCents),
        l.band,
        l.detail ?? '',
      ])
    )
  }

  return (
    <>
      <Toolbar onCsv={csv}>
        <label className={labelCls}>
          Group by
          <select className={inputCls} value={groupBy} onChange={(e) => set({ by: e.target.value === 'client' ? null : e.target.value })}>
            <option value="client">Client</option>
            <option value="job">Billing job</option>
            <option value="person">Person</option>
          </select>
        </label>
      </Toolbar>
      {error ? (
        <ErrorState message={error} />
      ) : data === null ? (
        <LoadingState what="WIP" />
      ) : (
        <>
          <section className={sectionCls}>
            <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
              Unbilled approved work as it stands today ({data.asAt}): hourly time at its stamped value, the fixed price of every period with approved time, and
              approved disbursements at cost. Fixed-price work is aged from the first work date in the period.
              {data.unpricedCents > 0 && (
                <>
                  {' '}
                  <strong className="text-eq-warning">{rand(data.unpricedCents)}</strong> is time on a job the client has no price for, valued by
                  the hour and flagged "no agreed price" (decision 6).
                </>
              )}
            </p>
            <table className="w-full border-collapse text-[13.5px]">
              <thead className="text-[12px] text-eq-neutral">
                <tr>
                  <th className={thCls}>{groupBy === 'client' ? 'Client' : groupBy === 'job' ? 'Billing job' : 'Person'}</th>
                  {AGE_BANDS.map((b) => (
                    <th key={b} className={numTh}>
                      {b}
                    </th>
                  ))}
                  <th className={numTh}>WIP</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.label} className="border-t border-eq-border">
                    <td className={tdCls}>{g.label}</td>
                    {AGE_BANDS.map((b) => (
                      <td key={b} className={numCls}>
                        {sumOr(g.lines.filter((l) => l.band === b))}
                      </td>
                    ))}
                    <td className={`${numCls} font-semibold`}>{rand(g.lines.reduce((a, l) => a + l.wipCents, 0))}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-eq-navy font-semibold">
                  <td className={tdCls}>Total</td>
                  {AGE_BANDS.map((b) => (
                    <td key={b} className={numCls}>
                      {data.totals.byBand[b] === 0 ? '' : rand(data.totals.byBand[b])}
                    </td>
                  ))}
                  <td className={numCls}>{rand(data.totals.wipCents)}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <section className={sectionCls}>
            <h2 className="mb-2 mt-0 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral">What makes it up</h2>
            {data.lines.length === 0 ? (
              <p className="m-0 text-[14px] text-eq-neutral">No unbilled approved work.</p>
            ) : (
              <table className="w-full border-collapse text-[13px]">
                <thead className="text-[12px] text-eq-neutral">
                  <tr>
                    <th className={thCls}>Client</th>
                    <th className={thCls}>Kind</th>
                    <th className={thCls}>Billing job</th>
                    <th className={thCls}>Period or date</th>
                    <th className={thCls}>Who</th>
                    <th className={numTh}>Hours</th>
                    <th className={numTh}>Time value</th>
                    <th className={numTh}>WIP</th>
                    <th className={thCls}>Age</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l, i) => (
                    <tr key={i} className="border-t border-eq-border">
                      <td className={tdCls}>{clientLabel(l.client)}</td>
                      <td className={tdCls}>
                        {l.kind === 'unpriced' ? <span className="text-eq-warning">{KIND_WORDS[l.kind]}</span> : KIND_WORDS[l.kind]}
                      </td>
                      <td className={tdCls}>
                        {l.job?.name ?? l.detail}
                        {l.job !== null && l.detail && <div className="text-[11.5px] text-eq-helper">{l.detail}</div>}
                      </td>
                      <td className={`${tdCls} whitespace-nowrap`}>{l.label}</td>
                      <td className={tdCls}>{l.people.join(', ')}</td>
                      <td className={numCls}>{l.hours ?? ''}</td>
                      <td className={numCls}>{l.kind === 'disbursement' ? '' : rand(l.valueCents)}</td>
                      <td className={`${numCls} font-semibold`}>{rand(l.wipCents)}</td>
                      <td className={tdCls}>{l.band}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  )
}

const sumOr = (lines: WipLine[]) => {
  const t = lines.reduce((a, l) => a + l.wipCents, 0)
  return t === 0 ? '' : rand(t)
}

// ---------------------------------------------------------------------------
// Recovery & write-off; Fixed price vs time
// ---------------------------------------------------------------------------

function RecoveryTab({ params, set, margin }: TabProps & { margin: boolean }) {
  const today = todaySast()
  const to = params.get('to') ?? today
  const from = params.get('from') ?? `${to.slice(0, 4)}-01-01`
  const load = useCallback(() => api.recoveryReport(from, to), [from, to])
  const { data, error } = useReport<RecoveryReport>(load)

  const csv = () => {
    if (data === null) return
    const head = ['Client number', 'Client', 'Billing job', 'Period', 'Period ended', 'People', 'Hours', 'Price']
    const tail = margin ? ['Time at cost', 'Margin'] : ['Time value', 'Write-off', 'Gain', 'Recovery %']
    downloadCsv(
      `${margin ? 'fixed-price-vs-time' : 'recovery'}-${data.from}-to-${data.to}.csv`,
      [...head, ...tail],
      data.periods.map((p) => [
        String(p.client.clientNumber),
        p.client.shortName,
        p.job.name,
        p.period.label,
        p.ended ? 'yes' : 'no',
        p.people.join('; '),
        p.hours,
        csvRand(p.priceCents),
        ...(margin
          ? [csvRand(p.costCents), csvRand(p.marginCents)]
          : [csvRand(p.valueCents), csvRand(p.writeOffCents), csvRand(p.gainCents), p.recoveryBp === null ? '' : (p.recoveryBp / 100).toFixed(2)]),
      ])
    )
  }

  return (
    <>
      <Toolbar onCsv={csv}>
        <label className={labelCls}>
          Periods from
          <input type="date" className={inputCls} value={from} onChange={(e) => set({ from: e.target.value || null })} />
        </label>
        <label className={labelCls}>
          to
          <input type="date" className={inputCls} value={to} onChange={(e) => set({ to: e.target.value || null })} />
        </label>
      </Toolbar>
      {error ? (
        <ErrorState message={error} />
      ) : data === null ? (
        <LoadingState what="recovery" />
      ) : (
        <section className={sectionCls}>
          <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
            {margin
              ? 'Each fixed-price period with approved time: the price against the time at COST rate — the margin per client and job.'
              : 'Each fixed-price period with approved time: the price against the time at charge-out value. Time above the price is written off, time below it is a gain; recovery is price ÷ time value.'}{' '}
            A period still running can change as more time is approved.
          </p>
          {data.periods.length === 0 ? (
            <p className="m-0 text-[14px] text-eq-neutral">No fixed-price periods with approved time in this range.</p>
          ) : (
            <table className="w-full border-collapse text-[13px]">
              <thead className="text-[12px] text-eq-neutral">
                <tr>
                  <th className={thCls}>Client</th>
                  <th className={thCls}>Billing job</th>
                  <th className={thCls}>Period</th>
                  <th className={numTh}>Hours</th>
                  <th className={numTh}>Price</th>
                  {margin ? (
                    <>
                      <th className={numTh}>Time at cost</th>
                      <th className={numTh}>Margin</th>
                    </>
                  ) : (
                    <>
                      <th className={numTh}>Time value</th>
                      <th className={numTh}>Write-off</th>
                      <th className={numTh}>Gain</th>
                      <th className={numTh}>Recovery</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.periods.map((p: FixedPeriodRow) => (
                  <tr key={`${p.client.id}|${p.job.id}|${p.period.start}`} className="border-t border-eq-border">
                    <td className={tdCls}>{clientLabel(p.client)}</td>
                    <td className={tdCls}>{p.job.name}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>
                      {p.period.label}
                      {!p.ended && <div className="text-[11.5px] text-eq-helper">still running</div>}
                    </td>
                    <td className={numCls}>{p.hours}</td>
                    <td className={numCls}>{rand(p.priceCents)}</td>
                    {margin ? (
                      <>
                        <td className={numCls}>{rand(p.costCents)}</td>
                        <td className={[numCls, p.marginCents < 0 ? 'text-eq-danger' : ''].join(' ')}>{rand(p.marginCents)}</td>
                      </>
                    ) : (
                      <>
                        <td className={numCls}>{rand(p.valueCents)}</td>
                        <td className={[numCls, p.writeOffCents > 0 ? 'text-eq-danger' : ''].join(' ')}>{p.writeOffCents === 0 ? '' : rand(p.writeOffCents)}</td>
                        <td className={[numCls, p.gainCents > 0 ? 'text-eq-success' : ''].join(' ')}>{p.gainCents === 0 ? '' : rand(p.gainCents)}</td>
                        <td className={numCls}>{pct(p.recoveryBp)}</td>
                      </>
                    )}
                  </tr>
                ))}
                <tr className="border-t-2 border-eq-navy font-semibold">
                  <td className={tdCls} colSpan={4}>
                    Total
                  </td>
                  <td className={numCls}>{rand(data.totals.priceCents)}</td>
                  {margin ? (
                    <>
                      <td className={numCls}>{rand(data.totals.costCents)}</td>
                      <td className={numCls}>{rand(data.totals.marginCents)}</td>
                    </>
                  ) : (
                    <>
                      <td className={numCls}>{rand(data.totals.valueCents)}</td>
                      <td className={numCls}>{rand(data.totals.writeOffCents)}</td>
                      <td className={numCls}>{rand(data.totals.gainCents)}</td>
                      <td className={numCls}>{pct(data.totals.recoveryBp)}</td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          )}
          {!margin && data.hourly.valueCents > 0 && (
            <p className="mb-0 mt-3 text-[12.5px] text-eq-neutral">
              Hourly work in this range — {data.hourly.hours} h, {rand(data.hourly.valueCents)} at charge-out
              {data.hourly.unpricedValueCents > 0 && <>, of which {rand(data.hourly.unpricedValueCents)} has no agreed price</>} — is not shown:
              its recovery can only be measured against what is invoiced, which arrives with T&amp;B-3.
            </p>
          )}
        </section>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Productivity
// ---------------------------------------------------------------------------

function ProductivityTab({ params, set }: TabProps) {
  const month = params.get('month') ?? todaySast().slice(0, 7)
  const load = useCallback(() => api.productivityReport(month), [month])
  const { data, error } = useReport<ProductivityReport>(load)

  const csv = () => {
    if (data === null) return
    const rows: string[][] = []
    for (const p of data.people) {
      for (const s of [...p.weeks, p.month]) {
        rows.push([
          p.person.name,
          s.label,
          s.from,
          s.to,
          s.expected === null ? '' : hoursOf(s.expected),
          hoursOf(s.leave),
          s.available === null ? '' : hoursOf(s.available),
          hoursOf(s.chargeable),
          hoursOf(s.nonChargeable),
          hoursOf(s.pending),
          s.productivityBp === null ? '' : (s.productivityBp / 100).toFixed(2),
        ])
      }
    }
    downloadCsv(
      `productivity-${data.month.slice(0, 7)}.csv`,
      ['Person', 'Span', 'From', 'To', 'Expected h', 'Leave h', 'Available h', 'Chargeable h', 'Non-chargeable h', 'Not yet approved h', 'Productivity %'],
      rows
    )
  }

  return (
    <>
      <Toolbar onCsv={csv}>
        <label className={labelCls}>
          Month
          <input type="month" className={inputCls} value={month} onChange={(e) => set({ month: e.target.value || null })} />
        </label>
      </Toolbar>
      {error ? (
        <ErrorState message={error} />
      ) : data === null ? (
        <LoadingState what="productivity" />
      ) : (
        <section className={sectionCls}>
          <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
            Chargeable hours ÷ available hours. Available = each person's working week for the days counted, less public holidays, less approved
            leave (decision 8). Only approved time counts; time not yet approved is shown apart. Counted through {data.through}.
          </p>
          <table className="w-full border-collapse text-[13px]">
            <thead className="text-[12px] text-eq-neutral">
              <tr>
                <th className={thCls}>Person</th>
                <th className={thCls}>Span</th>
                <th className={numTh}>Expected</th>
                <th className={numTh}>Leave</th>
                <th className={numTh}>Available</th>
                <th className={numTh}>Chargeable</th>
                <th className={numTh}>Other</th>
                <th className={numTh}>Not approved</th>
                <th className={numTh}>Productivity</th>
              </tr>
            </thead>
            <tbody>
              {data.people.map((p) =>
                [...p.weeks, p.month].map((s: ProductivitySpan, i) => (
                  <tr
                    key={`${p.person.id}|${s.from}|${i}`}
                    className={['border-t', i === p.weeks.length ? 'border-eq-border font-semibold' : 'border-eq-border-subtle', i === 0 ? 'border-t-eq-border' : ''].join(' ')}
                  >
                    <td className={tdCls}>{i === 0 ? p.person.name : ''}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{s.label}</td>
                    <td className={numCls}>{s.expected === null ? <span className="text-eq-helper">no working week</span> : hoursOf(s.expected)}</td>
                    <td className={numCls}>{s.leave === 0 ? '' : hoursOf(s.leave)}</td>
                    <td className={numCls}>{s.available === null ? '—' : hoursOf(s.available)}</td>
                    <td className={numCls}>{hoursOf(s.chargeable)}</td>
                    <td className={numCls}>{s.nonChargeable === 0 ? '' : hoursOf(s.nonChargeable)}</td>
                    <td className={`${numCls} text-eq-helper`}>{s.pending === 0 ? '' : hoursOf(s.pending)}</td>
                    <td className={numCls}>{pct(s.productivityBp)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  )
}
